/**
 * PsicoSalud — Panel de trabajo del profesional.
 *
 * Lo primero que ve el profesional al abrir el sistema: la fecha y el
 * saludo en encabezado, los indicadores del día (consultas de hoy,
 * próximas, pacientes activos), y la grilla de hoy con acceso directo
 * a sala remota y al historial clínico.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { appointmentsApi, patientsApi } from '@/api';
import { useAuth } from '@/context/AuthContext';
import EmptyState, { IconCalendar } from '@/components/EmptyState';
import Skeleton from '@/components/Skeleton';
import { getGreetingPeriod } from '@/utils/timeOfDay';
import type { Appointment } from '@/types';

function startOfDay(date: Date): string {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy.toISOString();
}

function endOfDay(date: Date): string {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 999);
  return copy.toISOString();
}

function fechaHoyLarga(): string {
  return new Date().toLocaleDateString('es-PY', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [today, setToday] = useState<Appointment[]>([]);
  const [upcoming, setUpcoming] = useState<Appointment[]>([]);
  const [activePatients, setActivePatients] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // El saludo del dashboard cambia segun la hora local del navegador:
  // 05-12 manana, 12-19 tarde, 19-05 noche. Asi el profesional ve
  // "Buenas tardes, Lic. Maria" a las 15:00 en lugar de "Buenos dias".
  const greetingPeriod = getGreetingPeriod();

  useEffect(() => {
    const now = new Date();
    const inSevenDays = new Date(now);
    inSevenDays.setDate(inSevenDays.getDate() + 7);

    async function load() {
      try {
        const [todayList, weekList, patientPage] = await Promise.all([
          appointmentsApi.list({ date_from: startOfDay(now), date_to: endOfDay(now) }),
          appointmentsApi.list({ date_from: endOfDay(now), date_to: endOfDay(inSevenDays) }),
          patientsApi.list({ limit: 1 }),
        ]);
        setToday(todayList);
        setUpcoming(weekList);
        setActivePatients(patientPage.total);
      } catch {
        setError(t('errors.network'));
      } finally {
        setLoading(false);
      }
    }

    void load();
  }, [t]);

  const sortedToday = useMemo(
    () => [...today].sort((a, b) => a.start_time.localeCompare(b.start_time)),
    [today],
  );

  if (loading) {
    return (
      <>
        <header className="page-header with-flex">
          <div>
            <span className="page-eyebrow">{fechaHoyLarga()}</span>
            <h1>
              {t(`dashboard.greeting.${greetingPeriod}`, {
                name: user?.full_name ?? '',
              })}
            </h1>
          </div>
        </header>

        <div
          className="grid grid-3"
          style={{ marginBottom: 'var(--espacio-5)' }}
          aria-hidden="true"
        >
          <Skeleton.StatCard />
          <Skeleton.StatCard />
          <Skeleton.StatCard />
        </div>

        <div className="card">
          <div className="card-header">
            <h2>{t('dashboard.todayAppointments')}</h2>
          </div>
          <Skeleton.Table rows={5} columns={5} />
        </div>
      </>
    );
  }

  return (
    <>
      <header className="page-header with-flex">
        <div>
          <span className="page-eyebrow">{fechaHoyLarga()}</span>
          <h1>
            {t(`dashboard.greeting.${greetingPeriod}`, {
              name: user?.full_name ?? '',
            })}
          </h1>
        </div>
        <div className="page-acciones">
          <button className="secondary" onClick={() => navigate('/pacientes')}>
            {t('dashboard.newPatient')}
          </button>
          <button onClick={() => navigate('/agenda')} data-cy="dashboard-new-appointment">
            {t('dashboard.newAppointment')}
          </button>
        </div>
      </header>

      {error && <div className="notice notice-error">{error}</div>}

      <div className="grid grid-3 is-reveal-stagger" style={{ marginBottom: 'var(--espacio-5)' }}>
        <div className="card stat is-interactive">
          <div className="stat-value" data-cy="stat-today">
            {today.length}
          </div>
          <div className="stat-label">{t('dashboard.todayAppointments')}</div>
        </div>
        <div className="card stat is-interactive">
          <div className="stat-value" data-cy="stat-upcoming">
            {upcoming.length}
          </div>
          <div className="stat-label">{t('dashboard.upcomingAppointments')}</div>
        </div>
        <div className="card stat is-interactive">
          <div className="stat-value" data-cy="stat-patients">
            {activePatients}
          </div>
          <div className="stat-label">{t('dashboard.activePatients')}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <span className="t-mayuscula" style={{ color: 'var(--tinta-suave)' }}>
              {t('dashboard.title')}
            </span>
            <h2>{t('dashboard.todayAppointments')}</h2>
          </div>
          <Link to="/agenda" className="is-button is-secondary is-pequeno">
            {t('common.search')} →
          </Link>
        </div>

        {sortedToday.length === 0 ? (
          <EmptyState
            icon={<IconCalendar />}
            title={t('dashboard.noAppointmentsTodayTitle')}
            description={t('dashboard.noAppointmentsTodayDescription')}
            action={{
              label: t('dashboard.newAppointment'),
              onClick: () => navigate('/agenda'),
            }}
          />
        ) : (
          <div className="table-wrapper">
            <table data-cy="dashboard-today-table">
              <thead>
                <tr>
                  <th style={{ width: '6rem' }}>{t('common.time')}</th>
                  <th>{t('agenda.patient')}</th>
                  <th style={{ width: '8rem' }}>{t('agenda.modality')}</th>
                  <th style={{ width: '8rem' }}>{t('agenda.status')}</th>
                  <th style={{ width: '14rem', textAlign: 'right' }}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedToday.map((appointment) => (
                  <tr key={appointment.id}>
                    <td className="t-numerico">
                      {new Date(appointment.start_time).toLocaleTimeString('es-PY', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td>
                      <Link
                        to={`/pacientes/${appointment.patient_id}`}
                        style={{ color: 'var(--tinta)', textDecoration: 'none', fontWeight: 'var(--peso-medio)' }}
                      >
                        {appointment.patient_name ?? '—'}
                      </Link>
                    </td>
                    <td>
                      <span className="badge badge-info">
                        {t(`agenda.modalities.${appointment.modality}`)}
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-neutral">
                        {t(`agenda.statuses.${appointment.status}`)}
                      </span>
                    </td>
                    <td>
                      <div className="button-row is-final">
                        {appointment.modality === 'REMOTA' && (
                          <Link
                            to={`/consultas/${appointment.id}/sesion`}
                            className="is-button is-pequeno"
                          >
                            {t('agenda.join')}
                          </Link>
                        )}
                        <Link
                          to={`/pacientes/${appointment.patient_id}/historial`}
                          className="is-button is-secondary is-pequeno"
                        >
                          {t('agenda.openRecord')}
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
